import React, { useMemo, useState, useCallback } from 'react'
import { Avatar, Divider, Flex, PseudoBox, Text } from '@chakra-ui/core'
import { IConfigRetailUser } from 'constants/interfaces'
import { useUserName } from 'hooks/useUserName'
import { getRoleBadgeProps, getRoleName } from './utils'
import { Edit, Shield, SquareChartGantt, Trash2, User } from 'lucide-react'
import { getUser } from 'redux/actions/admin'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'config/redux'
import { ActionMenu } from '../commonUI/ActionMenu'
import { useUserModals } from './UserModalsManagerContext'
import { isRtlSelector } from 'redux/selectors/general'
import { useSelector } from 'react-redux'
import useLocaleText from 'components/useLocaleText'
import { ConsoleBadge } from '../commonUI/ConsoleBadge'

interface UserCardProps {
  user: IConfigRetailUser
}

export const UserCard: React.FC<UserCardProps> = ({ user }) => {
  const { uid, user_group_role, sec_uid, desc } = user
  const isRtl = useSelector(isRtlSelector)
  const fullname = useUserName(uid)
  const dispatch: AppDispatch = useDispatch()
  const { openUserModal, openGroupModal, requestDeleteUser } = useUserModals()

  const [isLoading, setIsLoading] = useState(false)

  const { text, background, border } = getRoleBadgeProps(user_group_role)
  const roleName = getRoleName(user_group_role)

  const admin_uid = useLocaleText('admin_uid')
  const admin_sec_uid = useLocaleText('admin_sec_uid')
  const admin_desc = useLocaleText('admin_desc')
  const admin_edit_action = useLocaleText('admin_edit_action')
  const admin_edit_group_action = useLocaleText('edit_groups')
  const admin_delete_action = useLocaleText('admin_delete_action')

  const handleOpenUser = useCallback(async () => {
    try {
      setIsLoading(true)
      const userData = await dispatch(getUser(uid))
      if (userData) openUserModal(userData)
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, uid, openUserModal])

  const optionsList = useMemo(
    () => [
      {
        label: admin_edit_action,
        icon: <Edit size={16} />,
        onClick: handleOpenUser,
      },
      {
        label: admin_edit_group_action,
        icon: <Edit size={16} />,
        onClick: () => openGroupModal(user),
      },
      {
        label: admin_delete_action,
        icon: <Trash2 size={16} />,
        onClick: () => requestDeleteUser(user),
        color: 'red.500',
      },
    ],
    [
      admin_delete_action,
      admin_edit_action,
      admin_edit_group_action,
      handleOpenUser,
      openGroupModal,
      requestDeleteUser,
      user,
    ]
  )

  return (
    <PseudoBox
      borderWidth="1px"
      borderRadius="15px"
      overflow="hidden"
      _hover={{ boxShadow: 'md' }}
      transition="all 0.3s ease"
      display="flex"
      flexDirection="column"
      padding="10px"
      position="relative"
      opacity={isLoading ? 0.8 : 1}
      pointerEvents={isLoading ? 'none' : 'auto'}
    >
      <Flex alignItems="center" style={{ gap: '10px' }}>
        <Avatar size="md" src={user.profile_img_url} name={fullname} />
        <Flex flexDirection="column" justifyContent="center" style={{ gap: '5px' }}>
          <Text fontWeight={600} fontSize="16px" maxW={{ base: '100px', md: '200px', lg: '300px' }} isTruncated>
            {fullname}
          </Text>
          <ConsoleBadge borderColor={border} color={text} bg={background}>
            {roleName}
          </ConsoleBadge>
        </Flex>
      </Flex>

      <Divider my="10px" />

      <Flex flexDirection="column" style={{ gap: '10px' }}>
        <Flex alignItems="center" style={{ gap: '10px' }} fontSize="12px" fontWeight={500} dir={isRtl ? 'rtl' : 'ltr'}>
          <User size={18} strokeWidth={2.5} />
          <Text>{`${admin_uid}: ${uid}`}</Text>
        </Flex>
        <Flex alignItems="center" style={{ gap: '10px' }} fontSize="12px" fontWeight={500} dir={isRtl ? 'rtl' : 'ltr'}>
          <Shield size={18} strokeWidth={2.5} />
          <Text>{`${admin_sec_uid}: ${sec_uid || 'N/A'}`}</Text>
        </Flex>
        <Flex alignItems="center" style={{ gap: '10px' }} fontSize="12px" fontWeight={500} dir={isRtl ? 'rtl' : 'ltr'}>
          <SquareChartGantt size={18} strokeWidth={2.5} />
          <Text>{`${admin_desc}: ${desc || 'N/A'}`}</Text>
        </Flex>
      </Flex>

      <ActionMenu
        isLoading={isLoading}
        tooltipText="Edit User"
        optionsList={optionsList}
        triggerProps={{ top: '10px', right: isRtl ? 'unset' : '10px', left: isRtl ? '10px' : 'unset' }}
      />
    </PseudoBox>
  )
}
